import React from 'react';
import { Target, Eye, Heart, Users, Lightbulb, Shield } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'Unwavering commitment to delivering the highest quality in every project.'
    },
    {
      icon: Heart,
      title: 'Sustainability',
      description: 'Environmental stewardship integrated into everything we do.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Building strong partnerships with clients and industry experts.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'Continuous learning and adoption of cutting-edge solutions.'
    },
    {
      icon: Shield,
      title: 'Safety',
      description: 'Prioritizing workplace safety and regulatory compliance.'
    }
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-1 rounded-full text-sm font-semibold mb-4">
            About Us
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Building Tomorrow's World,{' '}
            <span className="text-emerald-600">Sustainably</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            At Eco Enviro Engineers, we simplify complex engineering challenges through 
            collaboration, innovation, and unwavering commitment to environmental responsibility.
          </p>
        </div>

        {/* Vision and Mission */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white">
            <CardContent className="p-8">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To be the leading provider of sustainable engineering solutions, recognized 
                globally for our technical excellence, innovative approach, and positive 
                environmental impact. We envision a future where engineering and environmental 
                stewardship work in perfect harmony.
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white">
            <CardContent className="p-8">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To deliver innovative, sustainable, and efficient civil and environmental 
                solutions that exceed client expectations. Through collaboration with trusted 
                partners and continuous learning, we provide multidisciplinary expertise 
                ensuring quality, safety, and environmental responsibility.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Core Values */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Our Core Values
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="flex flex-col items-center text-center group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">
                    {value.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Collaborative Approach */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Collaborative Approach
            </h3>
            <p className="text-emerald-50 leading-relaxed text-lg">
              We believe in the power of partnership. By working closely with trusted industry 
              experts and maintaining strategic collaborations, we deliver comprehensive, 
              end-to-end solutions that address the unique needs of every project. Our 
              multidisciplinary approach ensures no challenge is too complex.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
